import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Train, Type} from '../../models/train';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {BehaviorSubject, merge, Observable, of, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {TrainTypeDataService} from '../../services/train-type-data.service';
import { FormValidationService } from '../../../../shared/services/form-validation.service';
import { TranslateService } from "@ngx-translate/core";
import { environment } from '../../../../../environments/environment';
import {ConfirmationDialogService} from '../../../../shared/services/confirmation-dialog.service';

@Component({
  selector: 'app-train-edit',
  template: require('./train-edit.component.html'),
  styles: [require('./train-edit.component.sass')]
})
export class TrainEditComponent implements OnInit {

  @ViewChild('trainEditModal') trainEditModal: ElementRef;

  @Input() projectId;

  @Output() saveTrain = new EventEmitter<Train>();

  train: Train | undefined;
  trainForm: FormGroup;
  trainEditModalReference: NgbModalRef;

  types$: Observable<Type[] | null>;
  private readonly refreshTrainTypeList$ = new BehaviorSubject(undefined);

  maxLengthOfficialId = 4; // TODO put in configuration

  languageChangeSubscription: Subscription;
  locale = environment.calendar.locales['en'];
  
  constructor(
      private  trainService: TrainTypeDataService,
      private fb: FormBuilder,
      private modalService: NgbModal,
      public formValidation: FormValidationService,
      private translate: TranslateService,
      private confirmationDialogService: ConfirmationDialogService
  ) {
    this.locale = environment.calendar.locales[this.translate.currentLang];
    this.types$ = this.refreshTrainTypeList$.pipe(
        switchMap(() => merge(
            of(null),
            this.trainService.getTrainsTypeslist(this.projectId))
        ));
  }

  ngOnInit(): void {
        // TODO show current date by default when adding a new train
        this.trainForm = this.fb.group({
          officialId: ['', [Validators.required, Validators.maxLength(45),  Validators.pattern("^[0-9]*$")] ],
          relativeId: ['', [Validators.required, Validators.maxLength(4), Validators.pattern(/^-?(0|[1-9]\d*)?$/)] ],
          name: ['', Validators.required],
          connectivityServer: [''],
          telemetryServer: [''],
          maintainer: ['', Validators.required],
          type: ['', Validators.required],
          releasedDate: ['']
        });
      this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
          this.locale = environment.calendar.locales[this.translate.currentLang];
        })
  }

  get officialId() { return this.trainForm.get('officialId'); }
  get relativeId() { return this.trainForm.get('relativeId'); }


  async showModal(train?: Train) {
    this.trainForm.reset();
    this.train = train;
    const relativeIdField = this.trainForm.get('relativeId') as FormControl;
    let trainData;
    if (this.train) { // train update

      relativeIdField.disable({onlySelf: true});

      trainData = {
        officialId: this.train.officialId,
        relativeId: this.train.relativeId,
        name: this.train.name,
        connectivityServer: this.train.connectivityServer,
        telemetryServer: this.train.telemetryServer,
        maintainer: this.train.maintainer,
        type: this.train.trainType,
        releasedDate: new Date(this.train.releasedDate),
        layout: this.train.layout,
        projectId: this.train.projectId
      };

    } else { // train create

      relativeIdField.enable();
      trainData = {
        projectId: this.projectId,
        releasedDate: new Date()
      };
    }
    this.trainForm.patchValue(trainData);
    this.trainEditModalReference = await this.modalService.open(this.trainEditModal, {
      size: 'xl',
      backdrop: 'static'
    });
  }

  save = async () => {
    if (this.trainForm.valid) {
      if(this.train && (this.train.trainType !== this.trainForm.value.type)){
       const confirmChanges = await this.confirmationDialogService.confirm(
            `${this.translate.instant('I18N_TRAIN.EDIT_TRAIN')}`,
            this.translate.instant('I18N_TRAIN.CHANGE_TRAIN_TYPE_CONFIRMATION'),
            'warning',
            this.translate.instant('I18N_GENERAL.SAVE'),
            this.translate.instant('I18N_GENERAL.CANCEL'),
            "lg",
           'success');
       if(!confirmChanges){
         return;
       }
      }
        // TODO change trainType field to 'type' in Train model similar to backend model
        const train: Train = {
          id: this.train ? this.train.id : undefined,
          ...this.train,
          officialId: this.trainForm.value.officialId,
          relativeId: this.trainForm.getRawValue().relativeId,
          name: this.trainForm.value.name,
          connectivityServer: this.trainForm.value.connectivityServer,
          telemetryServer: this.trainForm.value.telemetryServer,
          maintainer: this.trainForm.value.maintainer,
          trainType: this.trainForm.value.type,
          releasedDate: this.trainForm.value.releasedDate,
          projectId: this.projectId,
        };
        this.saveTrain.emit({...train});
        this.trainEditModalReference.close();
      }
      else {
      this.formValidation.validateAllFormFields(this.trainForm);
    }

  }

  cancel = () => {
    this.trainEditModalReference.close();
  }

  onKeyDownEvent(event: any, maxDigits: number){
    if(event.target.value.length == maxDigits){
      return false
    };
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }

}
